import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface SectionProps {
  children: ReactNode;
  className?: string;
  /**
   * Padding variant
   * - "default": Standard section padding (112px vertical)
   * - "sm": Smaller padding (80px vertical)
   * - "xs": Minimal padding (64px vertical)
   * - "none": No padding
   */
  padding?: "default" | "sm" | "xs" | "none";
  /**
   * Optional HTML id for anchor links
   */
  id?: string;
  /**
   * HTML element to render as
   */
  as?: "section" | "div" | "article" | "aside";
}

/**
 * Section component for page sections.
 * Full-width for background colors/images, vertical padding for spacing.
 *
 * Use Container inside for content width constraints.
 *
 * Mobile: 64px vertical, 20px horizontal padding
 * Desktop: 112px vertical, 64px horizontal padding
 *
 * @example
 * ```tsx
 * <Section className="bg-primary">
 *   <Container>
 *     <h2>Title</h2>
 *   </Container>
 * </Section>
 * ```
 */
export function Section({
  children,
  className,
  padding = "default",
  id,
  as: Component = "section",
}: SectionProps) {
  return (
    <Component
      id={id}
      className={cn(
        "w-full",
        "px-px-mobile md:px-px-tablet lg:px-px-desktop",
        {
          "py-section-y-mobile md:py-section-y-sm lg:py-section-y":
            padding === "default",
          "py-section-y-mobile md:py-section-y-sm":
            padding === "sm",
          "py-section-y-mobile": padding === "xs",
          "py-0": padding === "none",
        },
        className
      )}
    >
      {children}
    </Component>
  );
}
